import React, { useEffect, useState } from "react";
import { Upload, Save, Undo2 } from "lucide-react";
import { adminUpdateBranding, adminUploadLogo, adminUploadBrandingVideo } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useBranding } from "../../lib/branding";

export function BrandingSection() {
    const {
        appName,
        logoUrl,
        videoUploadUrl,
        videoAnalyzeUrl,
        videoEstimateUrl,
        refresh,
    } = useBranding();
    const [name, setName] = useState(appName);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    // iter-62: per-slot video upload progress (0..100) + busy flag.
    const [videoBusy, setVideoBusy] = useState({}); // {slot: pct}
    useEffect(() => {
        setName(appName);
    }, [appName]);
    const handleSaveName = async () => {
        setSaving(true);
        try {
            await adminUpdateBranding({ app_name: name });
            toast.success("Name updated");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSaving(false);
        }
    };
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            await adminUploadLogo(file);
            toast.success("Logo uploaded");
            refresh();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't upload logo.",
            );
        } finally {
            setUploading(false);
        }
    };
    const handleClearLogo = async () => {
        try {
            await adminUpdateBranding({ logo_url: "" });
            toast.success("Reset to default logo");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't reset.");
        }
    };
    const handleVideoUpload = async (slot, file) => {
        setVideoBusy((prev) => ({ ...prev, [slot]: 1 }));
        try {
            await adminUploadBrandingVideo(slot, file, (pct) =>
                setVideoBusy((prev) => ({ ...prev, [slot]: Math.max(1, pct) })),
            );
            toast.success(`${slot[0].toUpperCase() + slot.slice(1)} video replaced`);
            refresh();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't upload video.",
            );
        } finally {
            setVideoBusy((prev) => {
                const { [slot]: _drop, ...rest } = prev;
                return rest;
            });
        }
    };
    const handleResetVideo = async (slot) => {
        try {
            const field = `video_${slot}_url`;
            await adminUpdateBranding({ [field]: "" });
            toast.success(`${slot[0].toUpperCase() + slot.slice(1)} video reset`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't reset.");
        }
    };
    const logoPreview = logoUrl
        ? logoUrl.startsWith("http")
            ? logoUrl
            : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`
        : "";
    return (
        <section
            className="rounded-sm border border-[#2A3655] bg-[#0F1B3A] p-4 sm:p-5 space-y-5"
            data-testid="admin-branding"
        >
            <div>
                <h2 className="text-[12px] uppercase tracking-[0.16em] text-slate-300 font-bold">
                    App name
                </h2>
                <p className="mt-1 text-[12px] text-slate-500">
                    Shown in the browser tab + accessible label. Live across the app.
                </p>
                <div className="mt-2 flex gap-2">
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Steeltooth"
                        data-testid="admin-branding-name"
                        className="bg-[#0A1128] border-[#2A3655] text-white max-w-md"
                    />
                    <Button
                        onClick={handleSaveName}
                        disabled={saving}
                        data-testid="admin-branding-save-name"
                        className="bg-[#007AFF] hover:bg-[#0062cc]"
                    >
                        <Save className="w-3.5 h-3.5 mr-1" />
                        Save
                    </Button>
                </div>
            </div>
            <div>
                <h2 className="text-[12px] uppercase tracking-[0.16em] text-slate-300 font-bold">
                    Logo
                </h2>
                <p className="mt-1 text-[12px] text-slate-500">
                    PNG / JPG / WEBP / SVG. Max 5 MB. Replaces the default mark everywhere.
                </p>
                <div className="mt-2 flex items-center gap-3">
                    <div className="w-24 h-16 rounded-sm border border-[#2A3655] bg-[#0A1128] flex items-center justify-center overflow-hidden">
                        {logoPreview ? (
                            <img
                                src={logoPreview}
                                alt="Current logo"
                                className="max-w-full max-h-full object-contain"
                                data-testid="admin-branding-preview"
                            />
                        ) : (
                            <span className="text-[11px] text-slate-500">Default</span>
                        )}
                    </div>
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#2A3655] text-[12px] font-bold text-slate-200 hover:bg-[#1C253C] cursor-pointer">
                        <Upload className="w-3.5 h-3.5" />
                        {uploading ? "Uploading…" : "Upload logo"}
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            className="hidden"
                            onChange={handleLogoUpload}
                            data-testid="admin-branding-logo-input"
                            disabled={uploading}
                        />
                    </label>
                    {logoPreview && (
                        <button
                            type="button"
                            onClick={handleClearLogo}
                            data-testid="admin-branding-clear-logo"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#2A3655] text-[11px] font-bold text-slate-300 hover:bg-[#1C253C]"
                        >
                            <Undo2 className="w-3 h-3" />
                            Reset
                        </button>
                    )}
                </div>
            </div>

            {/* iter-62: three independent hero videos — Upload dialog,
                Analyze overlay, Estimate overlay. Each slot can be
                independently replaced or reset to the bundled default
                (/uploading.mp4). Max 100 MB, .mp4 / .webm / .mov. */}
            <div>
                <h2 className="text-[12px] uppercase tracking-[0.16em] text-slate-300 font-bold">
                    Hero videos
                </h2>
                <p className="mt-1 text-[12px] text-slate-500">
                    MP4 / WEBM / MOV. Max 100 MB each. Replace the video shown at the top of the Upload / Analyze / Estimate dialogs.
                </p>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <VideoSlotUploader
                        slot="upload"
                        label="Upload dialog"
                        url={videoUploadUrl}
                        busyPct={videoBusy.upload}
                        onUpload={(file) => handleVideoUpload("upload", file)}
                        onReset={() => handleResetVideo("upload")}
                    />
                    <VideoSlotUploader
                        slot="analyze"
                        label="Analyze overlay"
                        url={videoAnalyzeUrl}
                        busyPct={videoBusy.analyze}
                        onUpload={(file) => handleVideoUpload("analyze", file)}
                        onReset={() => handleResetVideo("analyze")}
                    />
                    <VideoSlotUploader
                        slot="estimate"
                        label="Estimate overlay"
                        url={videoEstimateUrl}
                        busyPct={videoBusy.estimate}
                        onUpload={(file) => handleVideoUpload("estimate", file)}
                        onReset={() => handleResetVideo("estimate")}
                    />
                </div>
            </div>
        </section>
    );
}

export function VideoSlotUploader({ slot, label, url, busyPct, onUpload, onReset }) {
    const previewSrc = url
        ? url.startsWith("http")
            ? url
            : `${process.env.REACT_APP_BACKEND_URL}${url}`
        : "/uploading.mp4";
    const isUploading =
        typeof busyPct === "number" && busyPct > 0 && busyPct < 100;
    return (
        <div
            className="rounded-sm border border-[#2A3655] bg-[#0A1128] overflow-hidden"
            data-testid={`admin-branding-video-slot-${slot}`}
        >
            <div className="aspect-video bg-black">
                <video
                    key={previewSrc}
                    src={previewSrc}
                    className="w-full h-full object-cover"
                    muted
                    autoPlay
                    loop
                    playsInline
                    preload="metadata"
                />
            </div>
            <div className="p-2.5 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] uppercase tracking-wider text-slate-300 font-bold truncate">
                        {label}
                    </div>
                    <span
                        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${
                            url
                                ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
                                : "border-[#2A3655] text-slate-500"
                        }`}
                    >
                        {url ? "custom" : "default"}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <label
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-sm border text-[11px] font-bold cursor-pointer transition-colors ${
                            isUploading
                                ? "border-[#2A3655] bg-[#2A3655] text-slate-400 cursor-wait"
                                : "border-[#2A3655] text-slate-200 hover:bg-[#1C253C]"
                        }`}
                    >
                        <Upload className="w-3 h-3" />
                        {isUploading ? `Uploading ${busyPct}%` : "Upload"}
                        <input
                            type="file"
                            accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = "";
                                if (f) onUpload(f);
                            }}
                            data-testid={`admin-branding-video-input-${slot}`}
                            disabled={isUploading}
                        />
                    </label>
                    {url && !isUploading && (
                        <button
                            type="button"
                            onClick={onReset}
                            data-testid={`admin-branding-video-reset-${slot}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#2A3655] text-[11px] font-bold text-slate-300 hover:bg-[#1C253C]"
                        >
                            <Undo2 className="w-3 h-3" />
                            Reset
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

